import React, { lazy, useRef } from "react";

// Code-splitting helpers. Every page used to be statically imported, so the whole app (calendar,
// PDF renderer, rich-text editor, billing, charts) shipped in one 5.7 MB bundle that had to
// download and parse before the Clients page could render. Pages and heavy dialogs now load on
// demand, and are prefetched in the background once the first page is up.

/**
 * React.lazy for a named export, plus a `.preload()` that fetches the chunk ahead of time
 * without ever throwing (used for idle prefetching).
 *
 *   const CalendarPage = lazyNamed(() => import("./Calendar"), "CalendarPage");
 */
export const lazyNamed = (factory, exportName) => {
  const Component = lazy(() =>
    factory().then((module) => ({ default: exportName ? module[exportName] : module.default }))
  );
  Component.preload = () => factory().catch(() => {});
  return Component;
};

/** Prefetch lazy components after the page has settled, one at a time, when the browser is idle. */
export const preloadWhenIdle = (components, delayMs = 2500) => {
  const idle = window.requestIdleCallback || ((cb) => setTimeout(cb, 200));
  const timer = setTimeout(() => {
    const queue = components.filter((c) => typeof c?.preload === "function");
    const next = () => {
      const c = queue.shift();
      if (!c) return;
      idle(() => Promise.resolve(c.preload()).finally(next));
    };
    next();
  }, delayMs);
  return () => clearTimeout(timer);
};

/**
 * True once `open` has ever been true. Lets a lazily loaded drawer/dialog mount on first open
 * and then stay mounted, so its close animation still plays.
 */
export const useHasOpened = (open) => {
  const opened = useRef(false);
  if (open) opened.current = true;
  return opened.current;
};

const isChunkLoadError = (error) =>
  /ChunkLoadError|Loading chunk|Loading CSS chunk|dynamically imported module/i.test(
    `${error?.name ?? ""} ${error?.message ?? ""}`
  );

/**
 * After a deploy, a tab left open can ask for a chunk that no longer exists. Instead of a blank
 * tab, offer a reload (never automatic: a clinician may have unsaved work in another tab).
 */
export class ChunkErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    if (!isChunkLoadError(error)) throw error;
    return (
      <div style={{ padding: 32, fontFamily: "inherit", color: "#1C2025" }}>
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 6 }}>A newer version of Bloom is available</div>
        <div style={{ fontSize: 13, color: "#6B7A90", marginBottom: 14 }}>
          This page couldn't load because the app was updated. Save any open work in other tabs, then reload.
        </div>
        <button
          type="button"
          onClick={() => window.location.reload()}
          style={{
            font: "inherit", fontSize: 13, fontWeight: 700, color: "#fff", background: "#0072E5",
            border: 0, borderRadius: 8, padding: "7px 14px", cursor: "pointer",
          }}
        >
          Reload
        </button>
      </div>
    );
  }
}
